import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { dispatch, getState } from "../../../../state/store";
import {
  removeLoadingLocationId,
  resetMarkets,
  setConsentsAppType,
  setConsentsMarket,
  setLoadingLocationId,
  setLocations,
  setLocationsError,
  setLocationsLoading,
  setMarketsError,
  setMarketsLoading,
  setSelectedApp,
  setSelectedAppMarketsToAdd,
  setSelectedAppMarketsToDelete
} from "../../../../state/ducks";
import { selectLocations, selectMarkets } from "../../../../state/selectors";

import {
  appsService,
  appUserService,
  locationsService,
  log,
  marketsService,
  notificationService,
  t
} from "../../../../services";
import { appUserRoles, routePaths, strCode } from "../../../../consts";
import { useValidateMarketConsents } from "../../hooks/useValidateMarketConsents";

import { FlexContainers, WrapperContainer } from "../../../../components";
import {
  MarketFeaturesModal,
  MarketsTabModal,
  MarketsTabTable,
  Stores
} from "../../../../containers";
import { LocalizationsModal } from "../../../../containers/AppsManagement/MarketsTab/components/LocalizationsModal/LocalizationsModal";

const MarketsTab = () => {
  const [isMarketsModalActive, setIsMarketsModalActive] = useState(false);
  const [isLocationsModalActive, setIsLocationsModalActive] = useState(false);
  const [isLocalizationsModalActive, setIsLocalizationsModalActive] = useState(false);

  const [isMarketFeaturesModalActive, setIsMarketFeaturesModalActive] = useState(false);
  const [featuresMarket, setFeaturesMarket] = useState(null);
  const [locationsMarket, setLocationsMarket] = useState("");
  const [localizationsMarket, setLocalizationsMarket] = useState(null);

  const markets = useSelector((store) => store.markets?.markets);
  const loading = useSelector((store) => store.markets?.loading);
  const selectedApp = useSelector((store) => store.apps?.selectedApp);
  const userMarkets = useSelector((store) => store.configs.userMarkets) || [];
  const defaultFeatures = useSelector((store) => store.apps?.selectedApp.defaultSettings);
  const appsMarkets = useSelector((store) => store.apps?.appsMarkets);

  const history = useHistory();

  const unassignedMarkets = useMemo(() => {
    const isAdmin = appUserService.isAdmin();

    const allMarkets = markets ?? [];
    const selectedMarkets = (selectedApp.marketConfigs ?? []).map((market) => market.market);

    return allMarkets.filter((market) => {
      if (!isAdmin) {
        return !selectedMarkets.includes(market.market) && userMarkets.includes(market.market);
      }

      return !selectedMarkets.includes(market.market);
    });
  }, [markets, selectedApp]);

  const isReadOnlyAccess = useMemo(() => {
    return !(
      appUserService.hasRole(appUserRoles.Admin) ||
      appUserService.hasRole(appUserRoles.ApplicationManager)
    );
  }, []);

  const fetchMarkets = useCallback(async () => {
    dispatch(setMarketsError(null));

    if (loading) {
      return;
    }

    dispatch(setMarketsLoading(true));

    try {
      dispatch(resetMarkets());
      await marketsService.fetchMarkets();
      await fetchStores();
      await appsService.fetchAppsMarkets([selectedApp]);
    } catch (err) {
      dispatch(setMarketsError(err));

      log.error("MarketsTab: Failed to fetch markets", err);

      notificationService.showError({
        title: t(strCode.FETCH_MARKETS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setMarketsLoading(false));
    }
  }, []);

  const fetchStores = useCallback(async () => {
    dispatch(setLocationsLoading(true));
    dispatch(setLocationsError(null));

    try {
      const marketsState = selectMarkets()(getState()) || [];

      await Promise.all(
        marketsState?.map(({ market }) => {
          return locationsService.fetchLocations(market);
        })
      );
    } catch (err) {
      dispatch(setLocationsError(err));

      log.error("MarketsTab: Failed to fetch stores", err);

      notificationService.showError({
        title: t(strCode.LOCATIONS_FETCH_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setLocationsLoading(false));
      dispatch(setLocationsError(false));
    }
  }, []);

  useEffect(() => {
    fetchMarkets();
  }, []);

  const marketsWithoutConsents = useValidateMarketConsents();

  const onSavingMarkets = (markets) => {
    const newMarkets = markets.map((market) => {
      return {
        appType: selectedApp.appType,
        market: market.market,
        settings: defaultFeatures
      };
    });

    dispatch(setSelectedAppMarketsToAdd(newMarkets));
  };

  const onDeleteStore = async (id) => {
    dispatch(setLoadingLocationId(id));
    dispatch(setLocationsError(null));

    try {
      await locationsService.deleteLocation(locationsMarket, id);

      notificationService.showSuccess({
        title: t(strCode.EDIT_LOCATION_SUCCESS_TITLE),
        message: t(strCode.DELETE_LOCATION_SUCCESS_INFO)
      });
    } catch (err) {
      dispatch(setLocationsError(err));

      log.error("MarketsTab: Failed to delete store", err);

      notificationService.showError({
        title: t(strCode.DELETE_LOCATION_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(removeLoadingLocationId(id));
      dispatch(setLocationsError(false));
    }
  };

  const onSaveStore = async (id, storeData) => {
    dispatch(setLoadingLocationId(id));
    dispatch(setLocationsError(null));

    try {
      storeData.market = locationsMarket;
      storeData.address = storeData?.address ?? null;

      if (id === -1) {
        const locationsByMarket = selectLocations(locationsMarket)(getState()) ?? [];

        if (!locationsByMarket.some((location) => location.id === -1)) {
          dispatch(
            setLocations({
              market: locationsMarket,
              locations: [{ id, ...storeData }, ...locationsByMarket]
            })
          );
        }

        delete storeData.id;

        await locationsService.createNewLocation(storeData);

        notificationService.showSuccess({
          title: t(strCode.CREATE_LOCATION_SUCCESS_TITLE),
          message: t(strCode.CREATE_LOCATION_SUCCESS_MESSAGE)
        });
      } else {
        storeData.id = id;
        await locationsService.updateLocation(storeData);

        notificationService.showSuccess({
          title: t(strCode.EDIT_LOCATION_SUCCESS_TITLE),
          message: t(strCode.EDIT_LOCATIONS_SUCCESS_MESSAGE)
        });
      }
    } catch (err) {
      dispatch(setLocationsError(err));

      throw err;
    } finally {
      dispatch(removeLoadingLocationId(id));
      dispatch(setLocationsError(false));
    }
  };

  const onSaveLocalizations = async (localizationsData) => {
    const marketLocalizations = localizationsData?.length
      ? {
          market: localizationsMarket,
          languages: localizationsData
        }
      : null;

    const newMarketLocalizations = [
      ...selectedApp?.marketLocalizations?.filter((el) => el.market !== localizationsMarket)
    ];

    if (marketLocalizations) {
      newMarketLocalizations.push(marketLocalizations);
    }

    dispatch(
      setSelectedApp({
        ...selectedApp,
        marketLocalizations: newMarketLocalizations
      })
    );
  };

  const onConsentsOpen = (market) => {
    const appName = selectedApp.name;
    const appType = selectedApp.appType;

    dispatch(setConsentsAppType(appType));
    dispatch(setConsentsMarket(market));

    history.push(`${routePaths.APPS}/${appName}${routePaths.CONSENTS}`);
  };

  const onLocationsOpen = (market) => {
    setLocationsMarket(market);
    setIsLocationsModalActive(true);
  };

  const onMarketDelete = (market) => {
    dispatch(
      setSelectedAppMarketsToDelete({
        appType: selectedApp.appType,
        market
      })
    );
  };

  const onFeaturesSettingsOpen = (marketName) => {
    let market = null;

    if (appsMarkets[selectedApp.appType]) {
      market = appsMarkets[selectedApp.appType].find((m) => m.market === marketName);
    }

    if (!appsMarkets[selectedApp.appType] || !market) {
      market = selectedApp.marketConfigs.find((m) => m.market === marketName);
    }

    setIsMarketFeaturesModalActive(true);
    setFeaturesMarket(market);
  };

  const onLocalizationsOpen = (market) => {
    setLocalizationsMarket(market);
    setIsLocalizationsModalActive(true);
  };

  const onModalClose = () => {
    setIsMarketFeaturesModalActive(false);
    setFeaturesMarket(null);
  };

  return (
    <WrapperContainer Container={FlexContainers.FlexColumn} mt={24} pr={24} width="100%">
      <MarketsTabTable
        markets={markets ?? []}
        marketsWithoutConsents={marketsWithoutConsents}
        openModal={() => setIsMarketsModalActive(true)}
        onConsentsOpen={onConsentsOpen}
        onLocationsOpen={onLocationsOpen}
        onMarketDelete={onMarketDelete}
        isReadOnlyAccess={isReadOnlyAccess}
        onFeaturesSettingsOpen={onFeaturesSettingsOpen}
        onLocalizationsOpen={onLocalizationsOpen}
      />

      <MarketsTabModal
        markets={unassignedMarkets}
        isActive={isMarketsModalActive}
        onModalClose={() => setIsMarketsModalActive(false)}
        onSavingMarkets={onSavingMarkets}
      />

      <Stores
        market={locationsMarket}
        active={isLocationsModalActive}
        setActive={setIsLocationsModalActive}
        isReadOnlyAccess={isReadOnlyAccess}
        onSaveStore={onSaveStore}
        onDeleteStore={onDeleteStore}
      />

      <MarketFeaturesModal
        isActive={isMarketFeaturesModalActive}
        setActive={onModalClose}
        selectedMarket={featuresMarket}
      />

      <LocalizationsModal
        market={localizationsMarket}
        isActive={isLocalizationsModalActive}
        setActive={setIsLocalizationsModalActive}
        onSaveLocalizations={onSaveLocalizations}
      />
    </WrapperContainer>
  );
};

export default MarketsTab;
